// The application wizard (routes /permits/new and /permits/:id/edit).
//
// Saving: create_permit_draft on the first save, then revise_permit_draft with the
// whole fields object. The expected row version is the version this local copy is
// based on (from the permit read, then from each save result), so a change made by
// someone else is refused by the server instead of being silently overwritten.
// Unsaved edits stay in memory across steps, are never replaced by background
// refreshes, and are copied to this device until they are saved.
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { BlockerList, blockerText, ErrorBanner } from '@/components/evidence'
import { Banner, Button, Card, cx, EmptyState, PageHeader, SectionTitle, Sheet, Spinner } from '@/components/ui'
import { PERMIT_STATES } from '@/domain/catalogue'
import type { Blocker, CommandArgs, DraftFields, Me, PermitBundle, Registry } from '@/domain/types'
import { PtwError } from '@/lib/commands'
import { fmtDateTime, fmtTime } from '@/lib/format'
import { useMe, usePermit, usePolicy, useRegistry, useSession } from '@/lib/queries'
import { Declaration, LinkButton } from './bits'
import { readStorage, useGuardedCommand, useServerNow, writeStorage } from './hooks'
import type { DraftBackup, StepKey } from './model'
import {
  backupKey,
  blockersForRoot,
  createMissing,
  draftSnapshot,
  EDITABLE_STATES,
  EMPTY_SNAPSHOT,
  groupBlockersByStep,
  latestReturn,
  normaliseDraftFields,
  parseBackup,
  parseStep,
  staleCurrentVersion,
  STEPS,
  stepInfo,
  stepForBlocker,
  SUBMIT_DECLARATION,
} from './model'
import { StepHazards, StepRoutes, StepScope, StepTasks, StepWork } from './steps'
import { DraftSummary, makeLookup } from './views'

export function DraftEditorPage() {
  const { id } = useParams()
  const session = useSession()
  // One editor per permit and persona: creating the draft or switching persona starts
  // a fresh editor; unsaved edits travel through the device copy.
  return <EditorLoader key={`${session.identityKey ?? '-'}:${id ?? 'new'}`} permitId={id ?? null} />
}

type EditorProps = {
  bundle: PermitBundle | null
  registry: Registry
  me: Me | null
  /** The last refresh of the permit failed; the editor works on the last loaded copy. */
  refreshFailed: boolean
  refetchBundle: () => Promise<PermitBundle | null>
}

function Frame({ title, permitId, children }: { title: string; permitId: string | null; children: ReactNode }) {
  return (
    <div>
      <PageHeader title={title} actions={permitId ? <LinkButton to={`/permits/${permitId}`} variant="secondary">Back to the permit</LinkButton> : undefined} />
      {children}
    </div>
  )
}

function EditorLoader({ permitId }: { permitId: string | null }) {
  const permitQ = usePermit(permitId ?? undefined)
  const registryQ = useRegistry()
  const meQ = useMe()
  if ((permitId && permitQ.isLoading) || registryQ.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }
  // A failed background refresh keeps the last copy (TanStack Query keeps data with
  // the error); only a first load without any data stops the editor.
  if (permitId && !permitQ.data) {
    return (
      <Frame title="Edit application" permitId={null}>
        <ErrorBanner error={permitQ.error ?? new Error('Permit not available')} />
      </Frame>
    )
  }
  if (!registryQ.data) {
    return (
      <Frame title="Permit application" permitId={permitId}>
        <ErrorBanner error={registryQ.error ?? new Error('Site register not available')} title="The site register could not be loaded" />
      </Frame>
    )
  }
  return (
    <EditorGate
      bundle={permitId ? (permitQ.data ?? null) : null}
      registry={registryQ.data}
      me={meQ.data ?? null}
      refreshFailed={Boolean(permitId && permitQ.error)}
      refetchBundle={async () => (await permitQ.refetch()).data ?? null}
    />
  )
}

/** Decides once, at opening, whether there is a draft to edit. */
function EditorGate(props: EditorProps) {
  const { bundle } = props
  const [mode] = useState<'edit' | 'sealed' | 'no_draft'>(() =>
    !bundle ? 'edit' : !EDITABLE_STATES.includes(bundle.permit.lifecycle_state) ? 'sealed' : !bundle.draft ? 'no_draft' : 'edit',
  )
  if (bundle && mode === 'sealed') {
    const st = PERMIT_STATES[bundle.permit.lifecycle_state]?.label ?? bundle.permit.lifecycle_state.replace(/_/g, ' ')
    return (
      <Frame title={`Application ${bundle.permit.permit_number}`} permitId={bundle.permit.id}>
        <Banner tone="info" title="This application cannot be edited now">
          Its status is: {st}. Only a draft, or an application returned for revision, can be changed. To change a submitted application, the
          Responsible Supervisor returns it for revision.
        </Banner>
      </Frame>
    )
  }
  if (bundle && mode === 'no_draft') {
    return (
      <Frame title={`Application ${bundle.permit.permit_number}`} permitId={bundle.permit.id}>
        <EmptyState title="The draft is not available to you">Open the permit to see what you can do.</EmptyState>
      </Frame>
    )
  }
  return <EditorForm {...props} />
}

type Synced = { snapshot: string; rowVersion: number | null; savedAt: string | null }
type SaveOptions = { trigger: 'manual' | 'step' | 'submit' | 'leave'; step?: StepKey; force?: boolean; expected?: number | null }
type SaveResult = { ok: boolean; rowVersion: number | null }
const FAIL: SaveResult = { ok: false, rowVersion: null }

function joinWords(items: string[]): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

function EditorForm({ bundle, registry, me, refreshFailed, refetchBundle }: EditorProps) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const session = useSession()
  const nowMs = useServerNow()
  const permitId = bundle?.permit.id ?? null
  const isNew = permitId === null
  // Local copies belong to one identity: the demo persona, or the account (user:<id>, 0022).
  const owner = session.accountMode ? session.identityKey : session.activePersona
  const storageKey = backupKey(session.sandboxCode, owner, permitId)

  // ---- local state -----------------------------------------------------------
  const [initialFields] = useState(() => normaliseDraftFields(bundle?.draft?.fields ?? {}, registry.equipment))
  const [fields, setFields] = useState<DraftFields>(initialFields)
  const [siteId, setSiteId] = useState<string | null>(bundle?.permit.site.id ?? null)
  const [permitClass, setPermitClass] = useState<'A' | 'B' | null>(bundle ? (bundle.permit.permit_class === 'A' || bundle.permit.permit_class === 'B' ? bundle.permit.permit_class : null) : null)
  const [synced, setSynced] = useState<Synced>(() => ({
    snapshot: draftSnapshot(initialFields),
    rowVersion: bundle?.permit.row_version ?? null,
    savedAt: bundle?.draft?.updated_at ?? null,
  }))
  const [offer, setOffer] = useState<DraftBackup | null>(() => {
    const copy = parseBackup(readStorage(storageKey))
    if (!copy) return null
    const same = draftSnapshot(copy.fields) === draftSnapshot(initialFields) && (!isNew || (copy.site_id === null && copy.permit_class === null))
    return same ? null : copy
  })
  const [step, setStep] = useState<StepKey>(() => parseStep(params.get('step')) ?? 'scope')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<PtwError | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [conflict, setConflict] = useState<{ serverVersion: number | null } | null>(null)
  const [autoPaused, setAutoPaused] = useState(false)
  const [lastSubmit, setLastSubmit] = useState<{ at: string; error: PtwError } | null>(null)
  const [declaredFor, setDeclaredFor] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const create = useGuardedCommand('create_permit_draft')
  const revise = useGuardedCommand('revise_permit_draft')
  const submit = useGuardedCommand('submit_permit')
  const pendingCreate = useRef<CommandArgs['create_permit_draft'] | null>(null)
  const savingRef = useRef(false)
  const allowNavRef = useRef(false)
  const suppressCopyRef = useRef(false)

  // ---- derived ---------------------------------------------------------------
  const snapshot = draftSnapshot(fields)
  const dirty = isNew ? snapshot !== EMPTY_SNAPSHOT || siteId !== null || permitClass !== null : snapshot !== synced.snapshot
  const liveEditable = !bundle || EDITABLE_STATES.includes(bundle.permit.lifecycle_state)
  const formDisabled = !liveEditable || offer !== null
  const changedElsewhere = !isNew && !saving && bundle !== null && synced.rowVersion !== null && bundle.permit.row_version > synced.rowVersion
  const declared = declaredFor !== null && declaredFor === snapshot
  const site = registry.sites.find((s) => s.id === siteId) ?? null
  const policyQ = usePolicy(bundle?.policy.version_id ?? site?.authority?.policy_version_id ?? null)
  const policy = policyQ.data ?? null
  const lookup = makeLookup(registry, bundle)

  const submitBlockers: Blocker[] = lastSubmit && lastSubmit.error.code === 'PW001' ? lastSubmit.error.blockers : []
  const counts: Partial<Record<StepKey, number>> = {}
  for (const b of submitBlockers) {
    const s = stepForBlocker(b)
    if (s) counts[s] = (counts[s] ?? 0) + 1
  }
  const errorsFor = (root: string) => blockersForRoot(submitBlockers, root)

  const latest = useRef({ fields, siteId, permitClass, dirty, rowVersion: synced.rowVersion, step, offer })
  useEffect(() => {
    latest.current = { fields, siteId, permitClass, dirty, rowVersion: synced.rowVersion, step, offer }
  })

  const copyText = (): string => {
    const l = latest.current
    const copy: DraftBackup = { v: 1, fields: l.fields, base_row_version: l.rowVersion, site_id: l.siteId, permit_class: l.permitClass, written_at: new Date().toISOString() }
    return JSON.stringify(copy)
  }

  // ---- device copy of unsaved edits -----------------------------------------
  useEffect(() => {
    if (offer || suppressCopyRef.current) return // keep the earlier copy until the person decides
    if (!dirty) {
      writeStorage(storageKey, null)
      return
    }
    const t = window.setTimeout(() => writeStorage(storageKey, copyText()), 400)
    return () => window.clearTimeout(t)
    // copyText reads the latest values through a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, fields, siteId, permitClass, dirty, offer, synced.rowVersion])

  useEffect(
    () => () => {
      // Leaving the editor (navigation, persona switch): keep unsaved edits on this device.
      const l = latest.current
      if (l.dirty && !l.offer && !suppressCopyRef.current) writeStorage(storageKey, copyText())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageKey],
  )

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      writeStorage(storageKey, copyText())
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, storageKey])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => latest.current.dirty && !allowNavRef.current && currentLocation.pathname !== nextLocation.pathname,
  )

  // ---- follow the server copy only when nothing local would be lost ----------
  useEffect(() => {
    if (!bundle || isNew || saving || !bundle.draft) return
    if (synced.rowVersion === null) {
      // A save result without a version: adopt the server's once it shows our fields.
      if (draftSnapshot(bundle.draft.fields) === synced.snapshot) setSynced((s) => ({ ...s, rowVersion: bundle.permit.row_version }))
      return
    }
    if (!dirty && !conflict && !offer && bundle.permit.row_version > synced.rowVersion) {
      const f = normaliseDraftFields(bundle.draft.fields, registry.equipment)
      setFields(f)
      setSynced({ snapshot: draftSnapshot(f), rowVersion: bundle.permit.row_version, savedAt: bundle.draft.updated_at })
    }
  }, [bundle, isNew, saving, dirty, conflict, offer, synced.rowVersion, synced.snapshot])

  // ---- saving ----------------------------------------------------------------
  async function saveNew(opts: SaveOptions, sent: DraftFields): Promise<SaveResult> {
    let args = pendingCreate.current
    if (!args) {
      if (!siteId || !permitClass || !sent.site_area_id || !sent.work_type_id) {
        setNotice(`Not saved yet. To create the draft, choose ${joinWords(createMissing(siteId, permitClass, sent))} in step 1.`)
        return FAIL
      }
      args = { site_id: siteId, permit_class: permitClass, fields: sent }
    }
    savingRef.current = true
    setSaving(true)
    setSaveError(null)
    try {
      const res = await create.run(args)
      pendingCreate.current = null
      const newId = typeof res.permit_id === 'string' ? res.permit_id : null
      const rv = typeof res.row_version === 'number' ? res.row_version : null
      suppressCopyRef.current = true
      writeStorage(storageKey, null)
      const l = latest.current
      if (newId && draftSnapshot(l.fields) !== draftSnapshot(args.fields)) {
        // Typed while the draft was being created: hand those edits to the editor that opens next.
        const copy: DraftBackup = { v: 1, fields: l.fields, base_row_version: rv, site_id: args.site_id, permit_class: args.permit_class, written_at: new Date().toISOString() }
        writeStorage(backupKey(session.sandboxCode, owner, newId), JSON.stringify(copy))
      }
      allowNavRef.current = true
      if (!newId) {
        navigate('/permits', { replace: true })
        return FAIL
      }
      if (opts.trigger !== 'leave') navigate(`/permits/${newId}/edit?step=${opts.step ?? l.step}`, { replace: true })
      return { ok: true, rowVersion: rv }
    } catch (e) {
      const err = PtwError.from(e)
      // A request that may have reached the server is repeated exactly, so no second draft is created.
      if (err.code === 'NETWORK') pendingCreate.current = args
      setSaveError(err)
      return FAIL
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  async function save(opts: SaveOptions): Promise<SaveResult> {
    if (savingRef.current) return FAIL
    if (!liveEditable) {
      setNotice('This application can no longer be edited, so nothing was saved.')
      return FAIL
    }
    const sent = normaliseDraftFields(fields)
    const sentSnap = JSON.stringify(sent)
    setNotice(null)
    if (!permitId) return saveNew(opts, sent)
    if (!opts.force && sentSnap === synced.snapshot && synced.rowVersion !== null) return { ok: true, rowVersion: synced.rowVersion }
    const expected = opts.expected ?? synced.rowVersion
    if (expected === null) {
      setNotice('Still waiting for the saved version number from the server. Try again in a moment.')
      return FAIL
    }
    savingRef.current = true
    setSaving(true)
    setSaveError(null)
    try {
      const res = await revise.run({ permit_id: permitId, fields: sent }, expected)
      const rv = typeof res.row_version === 'number' ? res.row_version : null
      setSynced({ snapshot: sentSnap, rowVersion: rv, savedAt: new Date().toISOString() })
      setConflict(null)
      setAutoPaused(false)
      return { ok: true, rowVersion: rv }
    } catch (e) {
      const err = PtwError.from(e)
      setSaveError(err)
      if (err.code !== 'NETWORK') setAutoPaused(true)
      if (err.code === 'PW002') setConflict({ serverVersion: staleCurrentVersion(err.detail) })
      return FAIL
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  function goTo(next: StepKey) {
    if (next === step) return
    setStep(next)
    window.scrollTo({ top: 0 })
    if (dirty && liveEditable && !autoPaused && !conflict && !changedElsewhere && !offer && !savingRef.current) void save({ trigger: 'step', step: next })
  }

  async function loadServerCopy() {
    const fresh = await refetchBundle()
    if (!fresh?.draft) {
      setNotice('The saved draft could not be loaded. Check the connection and try again.')
      return
    }
    const f = normaliseDraftFields(fresh.draft.fields, registry.equipment)
    setFields(f)
    setSynced({ snapshot: draftSnapshot(f), rowVersion: fresh.permit.row_version, savedAt: fresh.draft.updated_at })
    setConflict(null)
    setAutoPaused(false)
    setSaveError(null)
  }

  async function keepMine() {
    const fresh = await refetchBundle()
    if (!fresh) {
      setNotice('The saved draft could not be loaded. Check the connection and try again.')
      return
    }
    setConflict(null)
    await save({ trigger: 'manual', force: true, expected: fresh.permit.row_version })
  }

  function restoreCopy() {
    if (!offer) return
    setFields(offer.fields)
    if (isNew) {
      setSiteId(offer.site_id)
      setPermitClass(offer.permit_class)
    }
    setOffer(null)
  }

  function discardCopy() {
    writeStorage(storageKey, null)
    setOffer(null)
  }

  async function onSubmit() {
    if (!permitId || submitting) return
    setSubmitting(true)
    try {
      let expected = synced.rowVersion
      if (dirty || expected === null) {
        const r = await save({ trigger: 'submit', force: expected === null })
        if (!r.ok) return
        expected = r.rowVersion
      }
      if (expected === null) {
        setNotice('Still waiting for the saved version number from the server. Try again in a moment.')
        return
      }
      let res: Awaited<ReturnType<typeof submit.run>>
      try {
        res = await submit.run({ permit_id: permitId }, expected)
      } catch (e) {
        setLastSubmit({ at: new Date().toISOString(), error: PtwError.from(e) })
        return
      }
      setLastSubmit(null)
      suppressCopyRef.current = true
      writeStorage(storageKey, null)
      allowNavRef.current = true
      // The permit page confirms it ("Submitted for review").
      navigate(`/permits/${permitId}`, {
        replace: true,
        state: { submitted: { revision_number: typeof res.revision_number === 'number' ? res.revision_number : null, at: new Date(nowMs).toISOString() } },
      })
    } finally {
      setSubmitting(false)
    }
  }

  // ---- render ----------------------------------------------------------------
  const savedAtShown =
    bundle?.draft && synced.rowVersion !== null && bundle.permit.row_version === synced.rowVersion ? bundle.draft.updated_at : synced.savedAt
  const status = saving
    ? 'Saving...'
    : isNew
      ? dirty
        ? 'Not saved yet - a copy is kept on this device'
        : 'Not saved yet'
      : dirty
        ? 'Unsaved changes - a copy is kept on this device'
        : synced.rowVersion !== null
          ? `Saved v${synced.rowVersion}${savedAtShown ? ` at ${fmtTime(savedAtShown)} GST` : ''}`
          : 'Saved'
  const returned = bundle ? latestReturn(bundle) : null
  const returnedRevision = returned ? bundle?.revisions.find((r) => r.id === returned.revision_id) : null
  const reviseAction = bundle?.viewer.available_actions.find((a) => a.command === 'revise_permit_draft') ?? null
  const editAdvice = bundle && liveEditable && !reviseAction?.enabled ? (reviseAction?.reason ?? null) : undefined
  const info = stepInfo(step)
  const index = STEPS.findIndex((s) => s.key === step)
  const prev = index > 0 ? STEPS[index - 1] : null
  const next = index < STEPS.length - 1 ? STEPS[index + 1] : null
  const stepBlockers = step === 'review' ? [] : submitBlockers.filter((b) => stepForBlocker(b) === step)
  const stepProps = { fields, update: (fn: (f: DraftFields) => DraftFields) => setFields(fn), registry, policy, errors: errorsFor }

  return (
    <div>
      <PageHeader
        title={isNew ? 'New permit application' : `Application ${bundle?.permit.permit_number ?? ''}`}
        subtitle={isNew ? 'Class B - fill in each step, then submit for review.' : `${PERMIT_STATES[bundle?.permit.lifecycle_state ?? '']?.label ?? ''}`}
        actions={permitId ? <LinkButton to={`/permits/${permitId}`} variant="secondary">Back to the permit</LinkButton> : undefined}
      />

      <div className="space-y-3">
        {returned ? (
          <Banner tone="warn" title="Returned for revision">
            {returned.attestation.signer.display_name} returned revision {returnedRevision?.revision_number ?? ''} on {fmtDateTime(returned.attestation.server_signed_at)}
            {returned.change_disposition ? ` as a ${returned.change_disposition} change` : ''}: {returned.reason ? `"${returned.reason}"` : 'no reason shown'}. Fix it and
            submit it again. Reviews and approvals of the earlier revision do not carry over.
          </Banner>
        ) : null}

        {offer ? (
          <Banner tone="warn" title="Unsaved changes were found on this device">
            <p>
              From {fmtDateTime(offer.written_at)}, not saved to the server.
              {!isNew && offer.base_row_version !== null && offer.base_row_version !== synced.rowVersion
                ? ` The saved draft has changed since then (v${offer.base_row_version} to v${synced.rowVersion ?? '?'}); restoring and saving replaces the newer saved version.`
                : ''}{' '}
              Choose what to do before you continue.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button onClick={restoreCopy}>Restore my changes</Button>
              <Button variant="secondary" onClick={discardCopy}>
                Discard them
              </Button>
            </div>
          </Banner>
        ) : null}

        {refreshFailed ? (
          <Banner tone="warn" title="Connection problem">
            The permit could not be refreshed, so this page shows the last copy loaded. Your changes stay here and on this device; saving needs a connection.
          </Banner>
        ) : null}

        {editAdvice !== undefined ? (
          <Banner tone="warn" title="Editing this draft is not listed among your next steps">
            {editAdvice ? `The server says: ${editAdvice}. ` : ''}You can still make changes; the server checks your permission each time you save.
          </Banner>
        ) : null}

        {!liveEditable && bundle ? (
          <Banner tone="bad" title="This application can no longer be edited">
            Its status changed to: {PERMIT_STATES[bundle.permit.lifecycle_state]?.label ?? bundle.permit.lifecycle_state}. Your unsaved changes are kept on this device
            only.
          </Banner>
        ) : null}

        {conflict || (changedElsewhere && dirty) ? (
          <Banner tone="bad" title="Someone else saved a newer version of this draft">
            <p>
              The saved draft is now v{conflict?.serverVersion ?? bundle?.permit.row_version ?? '?'}; your copy is based on v{synced.rowVersion ?? '?'}. Your changes are
              still here and are not saved. Choose which version to keep.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => void loadServerCopy()}>
                Load the saved version (drop my changes)
              </Button>
              <Button onClick={() => void keepMine()} loading={saving}>
                Keep my version and save it
              </Button>
            </div>
          </Banner>
        ) : null}
      </div>

      <StepNav step={step} counts={counts} onGo={goTo} />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-white px-3 py-2" aria-live="polite">
        <p className="text-sm text-ink">
          <span className="font-semibold">{status}</span>
          {autoPaused && !saving ? <span className="block text-xs text-ink-soft">Automatic saving is paused after the last failure. Press Save to try again.</span> : null}
          {pendingCreate.current && !saving ? (
            <span className="block text-xs text-ink-soft">The last save may not have reached the server. Saving again repeats the same request, so no second draft is made.</span>
          ) : null}
        </p>
        <Button variant="secondary" onClick={() => void save({ trigger: 'manual' })} loading={saving} disabled={!liveEditable || offer !== null}>
          Save draft
        </Button>
      </div>
      {notice ? (
        <Banner tone="warn" className="mb-3">
          {notice}
        </Banner>
      ) : null}
      {saveError && !conflict ? (
        <div className="mb-3">
          <ErrorBanner error={saveError} title={saveError.code === 'NETWORK' ? 'Not saved - no answer from the server' : 'Not saved'} />
        </div>
      ) : null}

      <h2 className="display mb-3 text-xl font-bold text-ink">
        <span className="text-ink-faint">
          Step {info.no} of {STEPS.length}:
        </span>{' '}
        {info.title}
      </h2>

      {stepBlockers.length > 0 && lastSubmit ? (
        <Banner tone="warn" title="Fix before submitting again" className="mb-3">
          <p className="mb-2 text-xs text-ink-soft">From your submit attempt at {fmtDateTime(lastSubmit.at)}. Saved changes are checked again only when you submit.</p>
          <BlockerList blockers={stepBlockers} />
        </Banner>
      ) : null}

      {step === 'review' ? (
        <ReviewStep
          fields={fields}
          lookup={lookup}
          siteId={siteId}
          permitClass={permitClass}
          isNew={isNew}
          editable={liveEditable && offer === null && !conflict}
          lastSubmit={lastSubmit}
          declared={declared}
          onDeclared={(v) => setDeclaredFor(v ? snapshot : null)}
          onSubmit={() => void onSubmit()}
          submitting={submitting || saving}
          saveProblem={saveError && !conflict ? saveError : null}
          notice={notice}
          onGo={goTo}
        />
      ) : (
        <Card>
          <fieldset disabled={formDisabled} className="m-0 min-w-0 border-0 p-0">
            {step === 'scope' ? (
              <StepScope
                {...stepProps}
                isNew={isNew}
                siteId={siteId}
                siteName={bundle?.permit.site.name ?? null}
                onSite={(id) => {
                  if (id === siteId) return
                  setSiteId(id)
                  // Areas belong to a site: a new site clears the area and the areas affected.
                  setFields((f) => ({ ...f, site_area_id: null, affected_area_ids: [], location_attributes_confirmed: false }))
                }}
                permitClass={permitClass}
                onClass={setPermitClass}
                me={me}
              />
            ) : null}
            {step === 'work' ? <StepWork {...stepProps} nowMs={nowMs} me={me} /> : null}
            {step === 'tasks' ? <StepTasks {...stepProps} /> : null}
            {step === 'hazards' ? <StepHazards {...stepProps} /> : null}
            {step === 'routes' ? <StepRoutes {...stepProps} siteId={siteId} /> : null}
          </fieldset>
        </Card>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <Button variant="secondary" onClick={() => prev && goTo(prev.key)} disabled={!prev}>
          Back
        </Button>
        {next ? <Button onClick={() => goTo(next.key)}>Next: {next.short}</Button> : null}
      </div>

      <Sheet open={blocker.state === 'blocked'} title="Leave without saving?" onClose={() => blocker.reset?.()}>
        <p className="text-sm text-ink">
          Some changes are not saved to the server. If you leave, a copy stays on this device and is offered when you open this application again.
        </p>
        {notice ? (
          <Banner tone="warn" className="mt-3">
            {notice}
          </Banner>
        ) : null}
        {saveError ? (
          <div className="mt-3">
            <ErrorBanner error={saveError} title="Not saved" />
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => blocker.reset?.()}>
            Stay here
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              writeStorage(storageKey, copyText())
              allowNavRef.current = true
              blocker.proceed?.()
            }}
          >
            Leave without saving
          </Button>
          <Button
            loading={saving}
            disabled={!liveEditable || offer !== null}
            onClick={async () => {
              const r = await save({ trigger: 'leave' })
              if (r.ok) {
                allowNavRef.current = true
                blocker.proceed?.()
              }
            }}
          >
            Save and leave
          </Button>
        </div>
      </Sheet>
    </div>
  )
}

function StepNav({ step, counts, onGo }: { step: StepKey; counts: Partial<Record<StepKey, number>>; onGo: (s: StepKey) => void }) {
  return (
    <nav aria-label="Application steps" className="no-print -mx-4 mb-3 mt-4 overflow-x-auto px-4">
      <ol className="flex gap-2 pb-1">
        {STEPS.map((s) => {
          const current = s.key === step
          const n = counts[s.key] ?? 0
          return (
            <li key={s.key}>
              <button
                type="button"
                onClick={() => onGo(s.key)}
                aria-current={current ? 'step' : undefined}
                className={cx(
                  'flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-full border px-3 text-sm font-semibold',
                  current ? 'border-brand bg-brand text-white' : 'border-line-strong bg-white text-ink',
                )}
              >
                <span className={cx('inline-flex h-6 w-6 items-center justify-center rounded-full text-xs', current ? 'bg-white text-brand-dark' : 'bg-paper')}>{s.no}</span>
                {s.short}
                {n > 0 ? <span className="rounded-full border border-warn/40 bg-warn-light px-2 text-xs text-warn">{n} to fix</span> : null}
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

function ReviewStep({ fields, lookup, siteId, permitClass, isNew, editable, lastSubmit, declared, onDeclared, onSubmit, submitting, saveProblem, notice, onGo }: {
  fields: DraftFields
  lookup: ReturnType<typeof makeLookup>
  siteId: string | null
  permitClass: string | null
  isNew: boolean
  editable: boolean
  lastSubmit: { at: string; error: PtwError } | null
  declared: boolean
  onDeclared: (v: boolean) => void
  onSubmit: () => void
  submitting: boolean
  /** A save that failed while submitting (shown next to the button too). */
  saveProblem: PtwError | null
  notice: string | null
  onGo: (s: StepKey) => void
}) {
  const blockers = lastSubmit?.error.code === 'PW001' ? lastSubmit.error.blockers : []
  const groups = groupBlockersByStep(blockers)
  const listRef = useRef<HTMLDivElement | null>(null)
  // A refused submission moves the reader to the list of what to fix (it is at the top of
  // a long page, well above the button that was pressed).
  useEffect(() => {
    if (!lastSubmit || !listRef.current) return
    listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    listRef.current.focus({ preventScroll: true })
  }, [lastSubmit])
  const showList = () => {
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    listRef.current?.focus({ preventScroll: true })
  }
  return (
    <div className="space-y-4">
      <div ref={listRef} tabIndex={-1} className="outline-none" aria-live="polite">
      {lastSubmit && blockers.length > 0 ? (
        <Banner tone="warn" title={`Not submitted: ${blockers.length} thing${blockers.length === 1 ? '' : 's'} to fix`}>
          <p className="text-xs text-ink-soft">From your submit attempt at {fmtDateTime(lastSubmit.at)}. Nothing was signed or sealed.</p>
          {groups.map((g) => (
            <div key={g.step ?? 'general'} className="mt-3">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-ink">{g.step ? `Step ${stepInfo(g.step).no}: ${stepInfo(g.step).title}` : 'The whole application'}</p>
                {g.step ? (
                  <Button variant="secondary" onClick={() => g.step && onGo(g.step)}>
                    Go to step {stepInfo(g.step).no}
                  </Button>
                ) : null}
              </div>
              <BlockerList blockers={g.blockers} />
            </div>
          ))}
        </Banner>
      ) : lastSubmit ? (
        <ErrorBanner error={lastSubmit.error} title={lastSubmit.error.code === 'NETWORK' ? 'No answer from the server - check the permit before trying again' : 'Not submitted'} />
      ) : null}
      </div>

      <DraftSummary fields={fields} lookup={lookup} siteId={siteId} permitClass={permitClass} onEdit={onGo} />

      <Card>
        <SectionTitle>Submit for review</SectionTitle>
        <p className="mb-3 text-sm text-ink">
          Submitting saves any changes, then seals this version of the application and sends it to the Responsible Supervisor for the permit meeting. It does
          not authorise any work. The server checks everything and lists anything still missing.
        </p>
        {isNew ? (
          <Banner tone="info" className="mb-3">
            Save the draft first: choose the geographical area, class, unit and work type in step 1, then press Save draft.
          </Banner>
        ) : null}
        <Declaration
          text={SUBMIT_DECLARATION}
          checked={declared}
          onChange={onDeclared}
          disabled={isNew || !editable}
        />
        <p className="mt-2 text-xs text-ink-soft">Changing anything after you tick clears the tick, so you always sign what you last saw.</p>
        {/* The result of pressing the button, next to it (the full list is at the top). */}
        <div className="mt-3 space-y-2" aria-live="assertive">
          {submitting ? <p className="text-sm font-semibold text-ink">Saving and submitting...</p> : null}
          {!submitting && lastSubmit && blockers.length > 0 ? (
            <Banner tone="warn" title={`Not submitted: ${blockers.length} thing${blockers.length === 1 ? '' : 's'} to fix`}>
              <p>Nothing was signed or sealed. The first is: {blockerText(blockers[0])}</p>
              <Button variant="secondary" className="mt-2" onClick={showList}>
                Show everything to fix
              </Button>
            </Banner>
          ) : null}
          {!submitting && lastSubmit && blockers.length === 0 ? (
            <ErrorBanner error={lastSubmit.error} title={lastSubmit.error.code === 'NETWORK' ? 'No answer from the server - check the permit before trying again' : 'Not submitted'} />
          ) : null}
          {!submitting && !lastSubmit && saveProblem ? <ErrorBanner error={saveProblem} title="Not submitted: the changes could not be saved first" /> : null}
          {!submitting && !lastSubmit && !saveProblem && notice ? (
            <Banner tone="warn" title="Not submitted">
              {notice}
            </Banner>
          ) : null}
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={onSubmit} loading={submitting} disabled={!declared || isNew || !editable}>
            Sign and submit
          </Button>
        </div>
      </Card>
    </div>
  )
}
